import {
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
  IconFile,
  IconMicrophone,
  IconMusic,
  IconPhoto,
  IconPlus,
  IconSend,
  IconVideo,
} from '@tabler/icons-react'
import {
  listDeskTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import { FileText } from 'lucide-react'
import { toast } from 'sonner'
import 'yet-another-react-lightbox/styles.css'
import { useChatStore, selectCurrentTicketId } from '@/stores/chat.store'
import { cn } from '@/lib/utils'
import { TicketStatus } from '@/constants/ticket-status'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AudioRecorderWithVisualizer,
  type RecordingCompletePayload,
} from '@/components/audio-recorder-with-visualizer'
import { AttachmentCard } from './attachment-card'
import type { FormValues } from './types'
import { UnifiedPreview } from './unified-preview'
import { useAttachments } from './use-attachments'
import { useMessageSubmit } from './use-message-submit'
import { formatBytes } from './utils'

const formSchema = z.object({
  message: z.string().default(''),
})

export interface ChatInputRef {
  focus: () => void
}

export const ChatInput = forwardRef<ChatInputRef>((_props, ref) => {
  const currentTicketId = useChatStore(selectCurrentTicketId)
  const { data: deskTickets } = useSuspenseQuery(listDeskTicketsQuery)
  const { data: pendingTickets } = useSuspenseQuery(listPendingTicketsQuery)
  const currentTicket = [...deskTickets, ...pendingTickets].find(
    (t) => t.id === currentTicketId
  )
  const [popoverOpen, setPopoverOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const [documentViewOpen, setDocumentViewOpen] = useState(false)
  const [isRecordingMode, setIsRecordingMode] = useState(false)
  const [isSendingRecordedAudio, setIsSendingRecordedAudio] = useState(false)
  const [currentDocument, setCurrentDocument] = useState<{
    name: string
    size: number
  } | null>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus()
    },
  }))

  const { register, handleSubmit, reset, watch } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: '',
    },
  })

  const { ref: registerRef, ...registerRest } = register('message')

  useEffect(() => {
    if (currentTicket?.status === TicketStatus.IN_PROGRESS) {
      inputRef.current?.focus()
    }
  }, [currentTicket?.status])

  const messageValue = watch('message')

  const {
    attachments,
    getRootProps,
    getInputProps,
    isDragActive,
    openImagePicker,
    openVideoPicker,
    openDocumentPicker,
    openAudioPicker,
    handleRemoveAttachment,
    clearAttachments,
    revokeAttachmentPreviews,
  } = useAttachments({ hasTicket: !!currentTicket })

  const { submitMessage, submitRecordedAudio } = useMessageSubmit({
    ticketId: currentTicket!.id,
    contactId: currentTicket!.contact.id,
    whatsappId: currentTicket!.whatsappId,
    attachments,
    clearAttachments,
    revokeAttachmentPreviews,
    reset,
  })

  const handleImageSelect = () => {
    openImagePicker()
    setPopoverOpen(false)
  }

  const handleVideoSelect = () => {
    openVideoPicker()
    setPopoverOpen(false)
  }

  const handleDocumentSelect = () => {
    openDocumentPicker()
    setPopoverOpen(false)
  }

  const handleAudioSelect = () => {
    openAudioPicker()
    setPopoverOpen(false)
  }

  const handleOpenRecorder = () => {
    if (!currentTicket || isSendingRecordedAudio) return
    setPopoverOpen(false)
    setIsRecordingMode(true)
  }

  const handleRecordingError = (error: Error) => {
    setIsSendingRecordedAudio(false)
    setIsRecordingMode(false)
    toast.error(error.message || 'Não foi possível gravar o áudio.')
  }

  const handleRecordingCancel = () => {
    if (isSendingRecordedAudio) return
    setIsRecordingMode(false)
  }

  const handleRecordingComplete = async ({
    file,
    mimeType,
    durationSeconds,
  }: RecordingCompletePayload) => {
    setIsSendingRecordedAudio(true)

    try {
      await submitRecordedAudio(file, mimeType, durationSeconds)
    } finally {
      setIsSendingRecordedAudio(false)
      setIsRecordingMode(false)
    }
  }

  const onSubmit = handleSubmit(submitMessage)

  const canSend = useMemo(() => {
    return Boolean(
      currentTicket && (attachments.length > 0 || messageValue.trim())
    )
  }, [attachments.length, currentTicket, messageValue])

  const previewableAttachments = useMemo(() => {
    return attachments.filter(
      (a) =>
        a.type === 'image' ||
        a.type === 'sticker' ||
        a.type === 'video' ||
        (a.type === 'document' && a.file.type === 'application/pdf')
    )
  }, [attachments])

  const openPreview = (attachmentId: string) => {
    const index = previewableAttachments.findIndex((a) => a.id === attachmentId)
    if (index >= 0) {
      setPreviewIndex(index)
      setPreviewOpen(true)
    }
  }

  const openDocumentView = (
    attachment: object & { file: { name: string }; size: number }
  ) => {
    setCurrentDocument({
      name: attachment.file.name,
      size: attachment.size,
    })
    setDocumentViewOpen(true)
  }

  useEffect(() => {
    if (!currentTicket) {
      setIsRecordingMode(false)
      setIsSendingRecordedAudio(false)
    }
  }, [currentTicket])

  return (
    <>
      <form onSubmit={onSubmit} className='flex w-full flex-col gap-2'>
        <div
          {...getRootProps()}
          className={cn(
            'rounded-xl border bg-background p-2 shadow-md transition-all',
            isDragActive
              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
              : 'border-input/70'
          )}
        >
          <input {...getInputProps()} />

          {attachments.length > 0 && (
            <div className='mb-2 grid grid-cols-1 gap-3 sm:grid-cols-2'>
              {attachments.map((attachment) => (
                <AttachmentCard
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={handleRemoveAttachment}
                  onImageClick={() => openPreview(attachment.id)}
                  onDocumentClick={openDocumentView}
                />
              ))}
            </div>
          )}

          {isRecordingMode ? (
            <AudioRecorderWithVisualizer
              autoStart
              disabled={!currentTicket || isSendingRecordedAudio}
              onRecordingComplete={handleRecordingComplete}
              onRecordingError={handleRecordingError}
              onCancel={handleRecordingCancel}
              className='max-w-none'
            />
          ) : (
            <div className='flex w-full items-center gap-2'>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size='icon'
                    type='button'
                    variant='ghost'
                    className='h-9 w-9 shrink-0 rounded-full hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50'
                    disabled={!currentTicket}
                  >
                    <IconPlus size={20} className='stroke-muted-foreground' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent side='top' align='start' className='w-auto p-2'>
                  <div className='grid grid-cols-2 gap-2'>
                    <Button
                      type='button'
                      variant='ghost'
                      className='flex h-auto flex-col items-center gap-1.5 px-4 py-3'
                      onClick={handleImageSelect}
                    >
                      <div className='rounded-full bg-primary/10 p-2'>
                        <IconPhoto size={20} className='text-primary' />
                      </div>
                      <span className='text-xs font-medium'>Fotos</span>
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      className='flex h-auto flex-col items-center gap-1.5 px-4 py-3'
                      onClick={handleVideoSelect}
                    >
                      <div className='rounded-full bg-green-500/10 p-2'>
                        <IconVideo size={20} className='text-green-600' />
                      </div>
                      <span className='text-xs font-medium'>Vídeos</span>
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      className='flex h-auto flex-col items-center gap-1.5 px-4 py-3'
                      onClick={handleDocumentSelect}
                    >
                      <div className='rounded-full bg-blue-500/10 p-2'>
                        <IconFile size={20} className='text-blue-600' />
                      </div>
                      <span className='text-xs font-medium'>Arquivos</span>
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      className='flex h-auto flex-col items-center gap-1.5 px-4 py-3'
                      onClick={handleAudioSelect}
                    >
                      <div className='rounded-full bg-purple-500/10 p-2'>
                        <IconMusic size={20} className='text-purple-600' />
                      </div>
                      <span className='text-xs font-medium'>Áudio</span>
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <label className='flex-1'>
                <span className='sr-only'>caixa de texto</span>
                <Input
                  type='text'
                  placeholder='Digite uma mensagem...'
                  className='h-9 w-full border-none bg-transparent px-0 text-sm shadow-none focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50'
                  autoComplete='off'
                  {...registerRest}
                  ref={(e) => {
                    registerRef(e)
                    inputRef.current = e
                  }}
                  disabled={!currentTicket}
                />
              </label>

              <Button
                variant='ghost'
                size='icon'
                type='button'
                className='h-9 w-9 shrink-0 rounded-full hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50'
                disabled={!currentTicket || isSendingRecordedAudio}
                onClick={handleOpenRecorder}
              >
                <IconMicrophone size={20} className='stroke-muted-foreground' />
              </Button>

              <Button
                variant='ghost'
                size='icon'
                type='submit'
                className='h-9 w-9 shrink-0 rounded-full hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50'
                disabled={!canSend}
              >
                <IconSend size={20} className={cn(canSend && 'text-primary')} />
              </Button>
            </div>
          )}
        </div>
      </form>
      <UnifiedPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        attachments={previewableAttachments}
        initialIndex={previewIndex}
        onIndexChange={setPreviewIndex}
      />
      <Dialog open={documentViewOpen} onOpenChange={setDocumentViewOpen}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Documento</DialogTitle>
            <DialogDescription>
              Preview do documento selecionado
            </DialogDescription>
          </DialogHeader>
          {currentDocument && (
            <div className='flex flex-col items-center gap-4 py-4'>
              <div className='rounded-full bg-primary/10 p-6'>
                <FileText size={48} className='text-primary' />
              </div>
              <div className='text-center'>
                <p className='break-all text-sm font-medium'>
                  {currentDocument.name}
                </p>
                <p className='mt-1 text-xs text-muted-foreground'>
                  {formatBytes(currentDocument.size)}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
})

ChatInput.displayName = 'ChatInput'
